const DEFAULT_ADMIN_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:4200';

export const ADMIN_API_BASE_URL = (
  process.env.REACT_APP_ADMIN_API_BASE_URL ||
  DEFAULT_ADMIN_API_BASE_URL
).replace(/\/+$/, '');

export class AdminApiError extends Error {
  constructor(message, { code = 'ADMIN_REQUEST_FAILED', status = 0, requestId = null } = {}) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

async function adminRequest(path, {
  apiKey,
  reviewer,
  method = 'GET',
  body,
  signal,
} = {}) {
  if (!apiKey?.trim()) {
    throw new AdminApiError('Enter the admin API key.', { code: 'ADMIN_KEY_REQUIRED' });
  }

  let response;
  try {
    response = await fetch(`${ADMIN_API_BASE_URL}/api/v1/admin${path}`, {
      method,
      signal,
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${apiKey.trim()}`,
        ...(reviewer?.trim() ? { 'x-admin-actor': reviewer.trim() } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new AdminApiError('The admin service is temporarily unavailable.', {
      code: 'ADMIN_SERVICE_UNAVAILABLE',
    });
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // Proxy errors are not always JSON; the generic message below still applies.
  }

  if (!response.ok || payload?.data === undefined) {
    throw new AdminApiError(
      payload?.error?.message || 'The admin request could not be completed.',
      {
        code: payload?.error?.code,
        status: response.status,
        requestId: payload?.error?.requestId || payload?.requestId,
      },
    );
  }

  return payload.data;
}

export function listMappingProposals(apiKey, reviewer, {
  state = 'PENDING',
  carrier,
  date,
  limit = 100,
  offset = 0,
  signal,
} = {}) {
  if (!['PENDING', 'APPROVED', 'AUTO_APPROVED', 'REJECTED'].includes(state)) {
    return Promise.reject(new AdminApiError(
      'Select Pending, Approved, Auto-approved, or Rejected.',
      { code: 'ADMIN_STATE_UNSUPPORTED' },
    ));
  }

  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (state) query.set('state', state);
  if (carrier?.trim()) query.set('carrier', carrier.trim().toLowerCase());
  if (date) query.set('date', date);

  return adminRequest(`/mapping-proposals?${query.toString()}`, {
    apiKey,
    reviewer,
    signal,
  });
}

export function listShipmentStatuses(apiKey, reviewer, { signal } = {}) {
  return adminRequest('/shipment-statuses', { apiKey, reviewer, signal });
}

export function approveMappingProposal(apiKey, reviewer, proposalId, decision, { signal } = {}) {
  return adminRequest(`/mapping-proposals/${encodeURIComponent(proposalId)}/approve`, {
    apiKey,
    reviewer,
    method: 'POST',
    body: decision,
    signal,
  });
}

export function rejectMappingProposal(apiKey, reviewer, proposalId, notes, { signal } = {}) {
  return adminRequest(`/mapping-proposals/${encodeURIComponent(proposalId)}/reject`, {
    apiKey,
    reviewer,
    method: 'POST',
    body: { notes },
    signal,
  });
}

export function editAutoApprovedMapping(apiKey, reviewer, predictionId, decision, { signal } = {}) {
  return adminRequest(`/mapping-proposals/auto-approved/${encodeURIComponent(predictionId)}/edit`, {
    apiKey,
    reviewer,
    method: 'POST',
    body: decision,
    signal,
  });
}
